/**
 * MMO Left Sidebar - Shark-fin Empire Card
 * Mirror of the right-side communication panel shape (concave curve at top-right).
 * Figma: 243x508, #131026 bg, cyan 3px border at 15% opacity.
 * Entity detail is handled by CommandDock (bottom panel).
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { PlayerProgression } from '../../managers/MMOProgressionManager';
import { EventBus } from '../../utils/EventBus';
import {
  Colors, Fonts, Depth,
  drawSharkFinLeft,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';

// ── Panel dimensions ────────────────────────────────────────────
const PANEL_W = 243;
const PANEL_H = 620;

// ── Layout offsets (panel-relative) ─────────────────────────────
const INDENT           = 27;   // standard left indent
const EMPIRE_TITLE_Y   = 54;   // "Empire status" y
const EMPIRE_TO_STATS  = 24;   // gap between heading bottom and first stat
const STAT_SPACING     = 16;   // vertical gap between stat rows
const STAT_GAP         = 11;   // horizontal gap between label and value
const STATS_TO_TABS    = 24;   // gap between CREDIT bottom and tab labels
const TAB_LABEL_H      = 20;   // tab label height
const TAB_SEP_OFFSET   = 4;    // gap between tab labels bottom and separator
const CONTENT_TOP_PAD  = 14;   // gap between separator and tab content

// ── Colors ──────────────────────────────────────────────────────
const BG_COLOR     = 0x131026;
const BORDER_CYAN  = 0x00ffff;
const EMPIRE_COLOR = '#E0E8FF';

// ── Text styles ─────────────────────────────────────────────────
const STAT_LABEL_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '13px',
  color: '#8899BB',
  fontFamily: "'Share Tech Mono', 'Courier New', monospace",
  letterSpacing: 2,
};

const STAT_VALUE_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '13px',
  color: '#00FFFF',
  fontFamily: "'Orbitron', 'Arial Black', sans-serif",
  fontStyle: '900',
};

const TAB_ACTIVE_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '10px',
  color: '#FFFFFF',
  fontFamily: "'Orbitron', 'Arial Black', sans-serif",
  fontStyle: '900',
  letterSpacing: 2,
};

const TAB_INACTIVE_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '10px',
  color: '#8899BB',
  fontFamily: "'Orbitron', 'Arial Black', sans-serif",
  fontStyle: '900',
  letterSpacing: 2,
};

const BODY_LABEL_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontSize: '13px',
  color: '#8899BB',
  fontFamily: "'Share Tech Mono', 'Courier New', monospace",
  letterSpacing: 1,
};

// ── Number formatting helper ────────────────────────────────────
function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

export class MMOLeftSidebar extends Phaser.GameObjects.Container {
  private mmoState: MMOStateManager;

  // UI elements
  private background: Phaser.GameObjects.Graphics;
  private contentContainer: Phaser.GameObjects.Container;

  // Stat text refs for live updates
  private planetCountText?: Phaser.GameObjects.Text;
  private unitCountText?: Phaser.GameObjects.Text;
  private exploredText?: Phaser.GameObjects.Text;
  private creditText?: Phaser.GameObjects.Text;

  // Tab bar state
  private activeTab: string = 'fleet';

  // Fleet tab rebuild throttle (rebuild every N ticks, not every single tick)
  private fleetRebuildCounter = 0;

  // Fleet scroll state
  private fleetScrollY = 0;
  private fleetMaxScrollY = 0;
  private fleetScrollMask: Phaser.Display.Masks.GeometryMask | null = null;
  public fleetContentStartY = 0;

  // Collapse state
  private collapsed = false;
  private expandedX = 10; // final resting x when expanded
  private collapseArrow!: Phaser.GameObjects.Text;
  private collapseHitZone!: Phaser.GameObjects.Zone;

  constructor(scene: Phaser.Scene, x: number, y: number, mmoState: MMOStateManager) {
    super(scene, x, y);
    this.mmoState = mmoState;

    // Shark-fin background (curve at top-right, mirror of comm panel)
    this.background = scene.add.graphics();
    this.add(this.background);

    // Content container
    this.contentContainer = scene.add.container(0, 0);
    this.add(this.contentContainer);

    // Draw fixed-size panel background
    this.redrawBackground();

    // Fleet scroll via mouse wheel when hovering over sidebar
    scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: any, _dx: number, dy: number, _dz: number) => {
      if (!this.visible || this.collapsed) return;
      const localX = _pointer.x - this.x;
      const localY = _pointer.y - this.y;
      if (localX >= 0 && localX <= PANEL_W && localY >= 0 && localY <= PANEL_H) {
        this.handleWheel(dy);
      }
    });

    // Build the empire stats display
    this.buildDisplay();

    // Collapse/expand toggle arrow
    this.buildCollapseToggle();

    // Live-update stats each tick
    EventBus.on('mmo-tick-update', this.onTickUpdate, this);
    EventBus.on('player_progression_updated', this.onProgressionUpdated, this);

    this.setDepth(Depth.panel);
    scene.add.existing(this);
  }

  // ── Collapse / Expand ────────────────────────────────────────

  private buildCollapseToggle(): void {
    const arrowX = PANEL_W + 4;
    const arrowY = PANEL_H / 2;

    // Arrow text: ◀ when expanded (click to collapse left), ▶ when collapsed (click to expand right)
    this.collapseArrow = this.scene.add.text(arrowX, arrowY, '◀', {
      fontSize: '16px',
      color: '#00ffff',
      fontFamily: Fonts.heading,
      fontStyle: '900',
    });
    this.collapseArrow.setOrigin(0, 0.5);
    this.collapseArrow.setAlpha(0.5);
    this.add(this.collapseArrow);

    // Hit zone for the arrow
    this.collapseHitZone = this.scene.add.zone(arrowX - 6, arrowY - 16, 28, 32).setOrigin(0, 0);
    this.collapseHitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, 28, 32),
      Phaser.Geom.Rectangle.Contains,
    );
    this.add(this.collapseHitZone);

    this.collapseHitZone.on('pointerover', () => {
      this.collapseArrow.setAlpha(1);
      this.scene.input.setDefaultCursor('pointer');
    });
    this.collapseHitZone.on('pointerout', () => {
      this.collapseArrow.setAlpha(0.5);
      this.scene.input.setDefaultCursor('default');
    });
    this.collapseHitZone.on('pointerdown', () => {
      this.toggleCollapse();
    });
  }

  private toggleCollapse(): void {
    this.collapsed = !this.collapsed;

    if (this.collapsed) {
      // Slide panel left off-screen, keep arrow visible
      this.collapseArrow.setText('▶');
      this.scene.tweens.add({
        targets: this,
        x: -(PANEL_W - 2),
        duration: 300,
        ease: 'Power2',
      });
    } else {
      // Slide panel back in
      this.collapseArrow.setText('◀');
      this.scene.tweens.add({
        targets: this,
        x: this.expandedX,
        duration: 300,
        ease: 'Power2',
      });
    }
  }

  /** Called after the initial slide-in tween to record final x */
  public setExpandedX(x: number): void {
    this.expandedX = x;
  }

  // ── Drawing ──────────────────────────────────────────────────

  private redrawBackground(): void {
    this.background.clear();
    drawSharkFinLeft(this.background, 0, 0, PANEL_W, PANEL_H, {
      fillColor: BG_COLOR,
      fillAlpha: 1.0,
      borderColor: BORDER_CYAN,
      borderAlpha: 0.15,
      borderWidth: 3,
    });
  }

  // ── Main build ───────────────────────────────────────────────

  private buildDisplay(): void {
    this.contentContainer.removeAll(true);

    const planets = this.mmoState.getPlayerPlanets();
    const units = this.mmoState.getPlayerUnits();
    const resources = this.mmoState.getResources();
    const explored = this.mmoState.getExploredHexes().size;

    // ── "Empire status" heading ──
    const empireTitle = this.scene.add.text(INDENT, EMPIRE_TITLE_Y, I18n.t('sidebar.empire_status'), {
      fontSize: '16px',
      fontStyle: '900',
      color: EMPIRE_COLOR,
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    empireTitle.setOrigin(0, 0);
    this.contentContainer.add(empireTitle);

    // ── Stat rows ──
    // 24px gap from bottom of "Empire status" to first stat
    let y = EMPIRE_TITLE_Y + empireTitle.height + EMPIRE_TO_STATS;

    y = this.addStatRow(y, I18n.t('sidebar.stations'), fmt(planets.length), 'planet');
    y = this.addStatRow(y, I18n.t('sidebar.ships'), fmt(units.length), 'unit');
    y = this.addStatRow(y, I18n.t('sidebar.explored'), I18n.t('sidebar.explored_hexes', { count: fmt(explored) }), 'explored');
    // ISD balance row with Buy button
    const creditY = y;
    const creditLabel = this.scene.add.text(INDENT + 1, creditY, '$ISD', STAT_LABEL_STYLE);
    creditLabel.setOrigin(0, 0);
    this.contentContainer.add(creditLabel);
    this.creditText = this.scene.add.text(
      INDENT + 1 + creditLabel.width + STAT_GAP, creditY,
      fmt(resources), STAT_VALUE_STYLE,
    );
    this.creditText.setOrigin(0, 0);
    this.creditText.setShadow(0, 0, 'rgba(0,255,255,0.4)', 10);
    this.contentContainer.add(this.creditText);

    // Small "Buy" button next to ISD balance
    const buyX = INDENT + 1 + creditLabel.width + STAT_GAP + this.creditText.width + 8;
    const buyBtn = this.scene.add.text(buyX, creditY, 'Buy', {
      fontSize: '9px', fontStyle: 'bold', color: '#00ff88', fontFamily: Fonts.body,
      backgroundColor: '#1a3322', padding: { x: 4, y: 2 },
    }).setOrigin(0, 0).setInteractive({ useHandCursor: true });
    buyBtn.on('pointerdown', () => { window.open('/tokenomics.html#withdrawal-fees', '_blank'); });
    buyBtn.on('pointerover', () => { buyBtn.setColor('#44ffaa'); });
    buyBtn.on('pointerout', () => { buyBtn.setColor('#00ff88'); });
    this.contentContainer.add(buyBtn);

    const creditBottom = creditY + Math.max(creditLabel.height, this.creditText.height);

    // ── Tab bar ──
    const tabY = creditBottom + STATS_TO_TABS;
    this.buildTabBar(tabY);

    // ── Tab content area ──
    const contentY = tabY + TAB_LABEL_H + TAB_SEP_OFFSET + 3 + CONTENT_TOP_PAD;
    this.fleetContentStartY = contentY;
    if (this.activeTab === 'fleet') {
      // Create a dedicated scroll container for fleet items
      if (this.fleetScrollContainer) {
        this.fleetScrollContainer.removeAll(true);
        this.fleetScrollContainer.destroy();
      }
      this.fleetScrollContainer = this.scene.add.container(0, 0);
      this.add(this.fleetScrollContainer);
      const totalH = this.buildFleetContent(contentY);
      const visibleH = PANEL_H - contentY - 10;
      this.fleetMaxScrollY = Math.max(0, totalH - contentY - visibleH);
      this.setupFleetScroll(contentY, visibleH);
    } else {
      this.buildMissionsContent(contentY);
    }
  }

  // ── Stat row helper ─────────────────────────────────────────

  private addStatRow(y: number, label: string, value: string, key: string): number {
    const labelText = this.scene.add.text(INDENT + 1, y, label, STAT_LABEL_STYLE);
    labelText.setOrigin(0, 0);
    this.contentContainer.add(labelText);

    const valueText = this.scene.add.text(
      INDENT + 1 + labelText.width + STAT_GAP, y,
      value, STAT_VALUE_STYLE,
    );
    valueText.setOrigin(0, 0);
    valueText.setShadow(0, 0, 'rgba(0,255,255,0.4)', 10);
    this.contentContainer.add(valueText);

    // Store references for live updates
    if (key === 'planet') this.planetCountText = valueText;
    if (key === 'unit') this.unitCountText = valueText;
    if (key === 'explored') this.exploredText = valueText;

    return y + Math.max(labelText.height, valueText.height) + STAT_SPACING;
  }

  // ── Tab bar ─────────────────────────────────────────────────

  private buildTabBar(y: number): void {
    const tabs = [
      { key: 'fleet', label: I18n.t('sidebar.fleet') },
    ];

    // Measure tab widths first to center them with equal padding
    const tempTexts = tabs.map(t =>
      this.scene.add.text(0, 0, t.label, { ...TAB_ACTIVE_STYLE }),
    );
    const tabWidths = tempTexts.map(t => t.width);
    tempTexts.forEach(t => t.destroy());

    const totalTabW = tabWidths.reduce((a, b) => a + b, 0);
    const gap = 24;
    const totalContentW = totalTabW + gap * (tabs.length - 1);
    const startX = Math.floor((PANEL_W - totalContentW) / 2);

    let tabX = startX;
    const tabTexts: Phaser.GameObjects.Text[] = [];

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const isActive = tab.key === this.activeTab;

      const tabText = this.scene.add.text(
        tabX, y, tab.label,
        isActive ? { ...TAB_ACTIVE_STYLE } : { ...TAB_INACTIVE_STYLE },
      );
      tabText.setOrigin(0, 0);
      this.contentContainer.add(tabText);
      tabTexts.push(tabText);

      // Active underline (3px bright cyan line under active tab)
      if (isActive) {
        const underline = this.scene.add.graphics();
        underline.lineStyle(3, Colors.cyan, 0.85);
        underline.lineBetween(tabX, y + tabText.height + TAB_SEP_OFFSET, tabX + tabText.width, y + tabText.height + TAB_SEP_OFFSET);
        this.contentContainer.add(underline);
      }

      // Hit zone
      const hitW = tabText.width + 8;
      const hitZone = this.scene.add.zone(tabX - 4, y - 4, hitW, tabText.height + 12).setOrigin(0, 0);
      hitZone.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, hitW, tabText.height + 12),
        Phaser.Geom.Rectangle.Contains,
      );
      this.contentContainer.add(hitZone);

      const tabIndex = i;
      hitZone.on('pointerover', () => {
        if (tab.key !== this.activeTab) tabTexts[tabIndex].setColor('#FFFFFF');
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        if (tab.key !== this.activeTab) tabTexts[tabIndex].setColor('#8899BB');
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        this.activeTab = tab.key;
        this.buildDisplay();
      });

      tabX += tabText.width + gap;
    }

    // Full-width separator line (3px, cyan 15%) below tabs
    const sepY = y + TAB_LABEL_H + TAB_SEP_OFFSET;
    const sepLine = this.scene.add.graphics();
    sepLine.lineStyle(3, Colors.cyan, 0.15);
    sepLine.lineBetween(0, sepY, PANEL_W, sepY);
    this.contentContainer.add(sepLine);
  }

  // ── Fleet scroll ────────────────────────────────────────

  private fleetScrollContainer: Phaser.GameObjects.Container | null = null;

  private setupFleetScroll(contentY: number, visibleH: number): void {
    // Remove old mask
    if (this.fleetScrollMask && this.fleetScrollContainer) {
      this.fleetScrollContainer.clearMask();
      this.fleetScrollMask = null;
    }

    if (!this.fleetScrollContainer || this.fleetMaxScrollY <= 0) return;

    // Create scroll mask on the fleet scroll container only (not the whole sidebar)
    const wx = this.x;
    const wy = this.y + contentY;
    const shape = this.scene.make.graphics({});
    shape.fillRect(wx, wy, PANEL_W, visibleH);
    this.fleetScrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, shape);
    this.fleetScrollContainer.setMask(this.fleetScrollMask);

    this.fleetScrollY = 0;
    this.fleetScrollContainer.y = 0;
  }

  public handleWheel(deltaY: number): void {
    if (this.fleetMaxScrollY <= 0 || !this.fleetScrollContainer) return;
    this.fleetScrollY = Phaser.Math.Clamp(this.fleetScrollY + deltaY * 0.3, 0, this.fleetMaxScrollY);
    this.fleetScrollContainer.y = -this.fleetScrollY;
  }

  // ── Fleet tab content ────────────────────────────────────

  private buildFleetContent(startY: number): number {
    let y = startY;
    // Use the dedicated scroll container for fleet items (scrollable)
    const fc = this.fleetScrollContainer || this.contentContainer;

    const units = this.mmoState.getPlayerUnits();
    const allPlanets = this.mmoState.getAllPlanets();
    const allStations = this.mmoState.getAllStations();

    // ── Ships section ──
    const shipsHeader = this.scene.add.text(INDENT, y, I18n.t('sidebar.ships'), {
      fontSize: '9px',
      fontStyle: '900',
      color: '#667799',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    fc.add(shipsHeader);
    y += 16;

    if (units.length === 0) {
      const noShips = this.scene.add.text(INDENT, y, I18n.t('sidebar.no_ships'), {
        ...BODY_LABEL_STYLE, fontSize: '11px', color: '#445566',
      });
      fc.add(noShips);
      y += 20;
    } else {
      for (const unit of units) {

        const unitType = (unit.type || 'scout').toLowerCase();
        const customName = (unit as any).customName;
        const variantName = (unit as any).shipVariant;
        const displayName = customName
          ? customName
          : variantName
            ? variantName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
            : I18n.shipName(unitType);
        const isDocked = !!unit.dockedAtPlanetId;

        // Row background
        const rowH = 32;
        const rowBg = this.scene.add.graphics();
        fc.add(rowBg);

        // Ship name
        const nameText = this.scene.add.text(INDENT + 4, y + 4, displayName, {
          fontSize: '11px',
          fontStyle: '900',
          color: '#E0E8FF',
          fontFamily: Fonts.heading,
        });
        fc.add(nameText);

        // Status line
        let statusStr: string;
        let statusColor: string;
        if (isDocked) {
          // Find the name of what it's docked at
          const planet = allPlanets.find(p => p.id === unit.dockedAtPlanetId);
          const station = allStations.find(s => s.id === unit.dockedAtPlanetId);
          const locationName = planet ? (planet.name || I18n.t('sidebar.planet')) : station ? I18n.t('sidebar.station_label') : I18n.t('sidebar.docked');
          statusStr = `⚓ ${locationName}`;
          statusColor = '#FFD700';
        } else if (unit.movementOrder) {
          statusStr = I18n.t('sidebar.moving', { turns: unit.movementOrder.turnsRemaining });
          statusColor = '#00FF88';
        } else {
          statusStr = `(${unit.position.q}, ${unit.position.r})`;
          statusColor = '#667799';
        }

        const statusText = this.scene.add.text(INDENT + 4, y + 18, statusStr, {
          fontSize: '9px',
          color: statusColor,
          fontFamily: Fonts.body,
        });
        fc.add(statusText);

        // Right-side: HP (top-right) and CARGO (bottom-right)
        const rightEdge = PANEL_W - 20;
        const hpPct = unit.maxHp > 0 ? unit.currentHp / unit.maxHp : 1;
        const hpColor = hpPct > 0.5 ? '#00FF88' : hpPct > 0.25 ? '#FFAA00' : '#FF4444';

        const hpText = this.scene.add.text(rightEdge, y + 4, `HP ${Math.round(hpPct * 100)}%`, {
          fontSize: '9px', color: hpColor, fontFamily: Fonts.heading, fontStyle: '900',
        });
        hpText.setOrigin(1, 0);
        fc.add(hpText);

        const capacity = unit.cargoCapacity ?? 100;
        const used = unit.cargoUsed ?? 0;
        if (used > 0 && capacity > 0) {
          const cargoPct = used / capacity;
          const cargoColor = cargoPct >= 0.9 ? '#FF4444' : cargoPct >= 0.7 ? '#FFAA00' : '#8899AA';
          const cargoText = this.scene.add.text(rightEdge, y + 18, `CARGO ${Math.round(used)}/${capacity}`, {
            fontSize: '9px', color: cargoColor, fontFamily: Fonts.heading, fontStyle: '900',
          });
          cargoText.setOrigin(1, 0);
          fc.add(cargoText);
        }

        // Hit zone for clicking
        const hitZone = this.scene.add.zone(INDENT, y, PANEL_W - INDENT - 12, rowH).setOrigin(0, 0);
        hitZone.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, PANEL_W - INDENT - 12, rowH),
          Phaser.Geom.Rectangle.Contains,
        );
        fc.add(hitZone);

        const unitRef = unit;
        hitZone.on('pointerover', () => {
          rowBg.clear();
          rowBg.fillStyle(0x00ffff, 0.06);
          rowBg.fillRect(INDENT - 4, y, PANEL_W - INDENT - 8, rowH);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          rowBg.clear();
          this.scene.input.setDefaultCursor('default');
        });
        hitZone.on('pointerdown', () => {
          if (isDocked) {
            // Pan to the docked location and select the planet/station, then activate ship tab
            EventBus.emit('fleet-select-docked-unit', {
              unitId: unitRef.id,
              dockedAtId: unitRef.dockedAtPlanetId,
            });
          } else {
            // Pan to unit and select it
            EventBus.emit('fleet-select-unit', { unitId: unitRef.id });
          }
        });

        y += rowH + 4;

        // Separator
        const sep = this.scene.add.graphics();
        sep.lineStyle(1, 0x00ffff, 0.04);
        sep.lineBetween(INDENT, y, PANEL_W - 28, y);
        fc.add(sep);
        y += 4;
      }
    }

    // ── Stations section ──
    y += 8;
    const playerStations = allStations.filter(s => s.userId === this.mmoState.getUserId());

    const stationsHeader = this.scene.add.text(INDENT, y, I18n.t('sidebar.stations'), {
      fontSize: '9px',
      fontStyle: '900',
      color: '#667799',
      fontFamily: Fonts.heading,
      letterSpacing: 2,
    });
    fc.add(stationsHeader);
    y += 16;

    if (playerStations.length === 0) {
      const noStations = this.scene.add.text(INDENT, y, I18n.t('sidebar.no_stations'), {
        ...BODY_LABEL_STYLE, fontSize: '11px', color: '#445566',
      });
      fc.add(noStations);
    } else {
      for (const station of playerStations) {

        const stationLabel = station.stationType.charAt(0).toUpperCase() + station.stationType.slice(1);
        const rowH = 28;

        const rowBg = this.scene.add.graphics();
        fc.add(rowBg);

        // Station name
        const STATION_COLORS: Record<string, string> = {
          waypoint: '#00FFFF',
          depot: '#00FF88',
          fortress: '#FF8800',
          capital: '#FF0088',
        };
        const stationColor = STATION_COLORS[station.stationType] || '#00FFFF';

        const nameText = this.scene.add.text(INDENT + 4, y + 4, stationLabel, {
          fontSize: '11px',
          fontStyle: '900',
          color: stationColor,
          fontFamily: Fonts.heading,
        });
        fc.add(nameText);

        // Location
        const locText = this.scene.add.text(INDENT + 4, y + 18, `(${station.position.q}, ${station.position.r})`, {
          fontSize: '9px',
          color: '#667799',
          fontFamily: Fonts.body,
        });
        fc.add(locText);

        // Hit zone
        const hitZone = this.scene.add.zone(INDENT, y, PANEL_W - INDENT - 12, rowH).setOrigin(0, 0);
        hitZone.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, PANEL_W - INDENT - 12, rowH),
          Phaser.Geom.Rectangle.Contains,
        );
        fc.add(hitZone);

        const stationRef = station;
        hitZone.on('pointerover', () => {
          rowBg.clear();
          rowBg.fillStyle(0x00ffff, 0.06);
          rowBg.fillRect(INDENT - 4, y, PANEL_W - INDENT - 8, rowH);
          this.scene.input.setDefaultCursor('pointer');
        });
        hitZone.on('pointerout', () => {
          rowBg.clear();
          this.scene.input.setDefaultCursor('default');
        });
        hitZone.on('pointerdown', () => {
          EventBus.emit('fleet-select-station', { stationId: stationRef.id });
        });

        y += rowH + 4;

        // Separator
        const sep = this.scene.add.graphics();
        sep.lineStyle(1, 0x00ffff, 0.04);
        sep.lineBetween(INDENT, y, PANEL_W - 28, y);
        fc.add(sep);
        y += 4;
      }
    }

    return y;
  }

  // ── Missions tab content ─────────────────────────────────────

  private buildMissionsContent(startY: number): void {
    let y = startY;

    // Missions are not yet implemented on the server
    const noMissions = this.scene.add.text(
      PANEL_W / 2, y + 40,
      I18n.t('sidebar.no_missions'),
      {
        ...BODY_LABEL_STYLE,
        color: '#556677',
        fontSize: '16px',
        align: 'center',
      },
    );
    noMissions.setOrigin(0.5, 0);
    this.contentContainer.add(noMissions);

    const hint = this.scene.add.text(
      PANEL_W / 2, y + 90,
      I18n.t('sidebar.coming_soon'),
      {
        ...BODY_LABEL_STYLE,
        color: '#334455',
        fontSize: '14px',
        align: 'center',
      },
    );
    hint.setOrigin(0.5, 0);
    this.contentContainer.add(hint);
  }

  // ── Event handlers ────────────────────────────────────────────

  private onTickUpdate(): void {
    const planets = this.mmoState.getPlayerPlanets().length;
    const units = this.mmoState.getPlayerUnits().length;
    const explored = this.mmoState.getExploredHexes().size;
    const credits = this.mmoState.getResources();

    if (this.planetCountText) this.planetCountText.setText(fmt(planets));
    if (this.unitCountText) this.unitCountText.setText(fmt(units));
    if (this.exploredText) this.exploredText.setText(I18n.t('sidebar.explored_hexes', { count: fmt(explored) }));
    if (this.creditText) this.creditText.setText(fmt(credits));

    // Rebuild fleet tab every 5 ticks to reflect status changes (not every tick for perf)
    if (this.activeTab === 'fleet') {
      this.fleetRebuildCounter++;
      if (this.fleetRebuildCounter >= 5) {
        this.fleetRebuildCounter = 0;
        this.buildDisplay();
      }
    }
  }

  private onProgressionUpdated(_data: PlayerProgression): void {
    // Rebuild display when progression data arrives
    this.buildDisplay();
  }

  // ── Public API ─────────────────────────────────────────────────

  public updateDisplay(): void {
    this.buildDisplay();
  }

  public setConnectionStatus(_connected: boolean): void {
    // Kept as no-op for API compatibility.
  }

  // ── Cleanup ────────────────────────────────────────────────────

  public destroy(fromScene?: boolean): void {
    EventBus.off('mmo-tick-update', this.onTickUpdate, this);
    EventBus.off('player_progression_updated', this.onProgressionUpdated, this);
    super.destroy(fromScene);
  }
}
